import React, { useCallback, useEffect, useState } from "react";
import { PageHeader, Content } from "@/components/PageChrome";
import { TablePagination } from "@/components/TablePagination";
import { api, formatApiError } from "@/lib/api";
import { Activity, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm " +
  "focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none";

const METHODS = ["", "GET", "POST", "PUT", "PATCH", "DELETE"];

const PAGE_SIZE = 25;

/** Never hand a raw logged value to the DOM; it may be an object. */
function safe(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function methodChip(m) {
  const cls =
    m === "DELETE"
      ? "bg-red-50 text-red-700"
      : m === "POST"
        ? "bg-green-50 text-green-700"
        : m === "GET"
          ? "bg-blue-50 text-blue-700"
          : "bg-amber-50 text-amber-700";
  return <span className={`text-[11px] px-1.5 py-0.5 rounded num ${cls}`}>{safe(m)}</span>;
}

function statusChip(s) {
  const code = Number(s);
  const cls =
    code >= 500
      ? "bg-red-50 text-red-700"
      : code >= 400
        ? "bg-amber-50 text-amber-700"
        : code >= 300
          ? "bg-blue-50 text-blue-700"
          : "bg-green-50 text-green-700";
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded num ${cls}`} data-testid="activity-status">
      {safe(s)}
    </span>
  );
}

/**
 * Read-only view of the request-level activity log.
 *
 * This is the "what actually happened" trail (every authenticated call, bodies
 * redacted server-side), as distinct from the curated audit log.
 */
export default function AdminActivity() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filters, setFilters] = useState({ actor_id: "", method: "", path_contains: "", status: "" });
  // Every filter, status included, is applied by the API. Filtering rows in the
  // browser would narrow the visible page while `total` and the pager still
  // described the unfiltered result, so page 2 of a filtered view could be
  // empty even when matches exist.
  const [applied, setApplied] = useState({ actor_id: "", method: "", path_contains: "", status: "" });
  const load = useCallback(async (nextFilters, skip, size) => {
    setLoading(true);
    try {
      const params = { limit: size, skip: skip || 0, ...nextFilters };
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] == null) delete params[k];
      });
      const { data } = await api.get("/admin/activity", { params });
      if (Array.isArray(data)) {
        setEntries(data);
        setTotal(data.length);
      } else {
        const rows = Array.isArray(data?.entries) ? data.entries : [];
        setEntries(rows);
        setTotal(Number.isFinite(data?.total) ? data.total : rows.length);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(applied, 0, pageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters are passed in explicitly rather than read from `applied` state:
  // reading it here would use the previous render's value, because
  // setApplied() from the submit handler has not been applied yet.
  const submit = (e) => {
    e.preventDefault();
    setApplied(filters);
    setPage(0);
    load(filters, 0, pageSize);
  };

  const refresh = () => load(applied, page * pageSize, pageSize);

  const changePageSize = (n) => { setPageSize(n); setPage(0); load(applied, 0, n); };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <PageHeader
        overline="Administration"
        title="Activity Log"
        description="Every request made by a signed-in user, including who did it and what changed. Sensitive field values are never stored here."
        testid="page-admin-activity"
        actions={
          <button onClick={refresh}
                  className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
                  data-testid="activity-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        }
      />

      <Content>
        <form onSubmit={submit} className="card-flat p-4 mb-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-gray-600 block mb-1.5">Actor ID</label>
            <input className={inputCls} value={filters.actor_id} onChange={set("actor_id")}
                   placeholder="user id" data-testid="activity-filter-actor" />
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1.5">Method</label>
            <select className={inputCls} value={filters.method} onChange={set("method")}
                    data-testid="activity-filter-method">
              {METHODS.map((m) => (
                <option key={m} value={m}>{m || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1.5">Status codes</label>
            <input className={inputCls} value={filters.status} onChange={set("status")}
                   placeholder="403, 404" data-testid="activity-filter-status" />
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1.5">Path contains</label>
            <input className={inputCls} value={filters.path_contains} onChange={set("path_contains")}
                   placeholder="/documents" data-testid="activity-filter-q" />
          </div>
          <button type="submit" className="btn-primary h-10 text-sm inline-flex items-center justify-center gap-2"
                  data-testid="activity-filter-submit">
            <Search className="w-4 h-4" /> Filter
          </button>
        </form>

        {loading && entries.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <Activity className="w-7 h-7 text-gray-300 mx-auto" />
            <div className="text-sm text-gray-500 mt-2">Loading activity…</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <Activity className="w-7 h-7 text-gray-300 mx-auto" />
            <div className="text-sm font-medium mt-2.5">No activity recorded</div>
            <p className="text-xs text-gray-500 mt-1">
              Requests appear here as soon as users start working.
            </p>
          </div>
        ) : (
          <div className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="activity-table">
                <thead>
                  <tr className="border-b border-[var(--border)] text-left">
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">When</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Actor</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Request</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={safe(e.id)} className="border-b border-[var(--border)] last:border-0 align-top"
                        data-testid="activity-row">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="truncate">{safe(e.actor_email) || "—"}</div>
                        <div className="text-gray-400 num">{safe(e.actor_role) || "user"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {methodChip(e.method)}
                          <span className="text-xs truncate num max-w-[260px]">{safe(e.path)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{statusChip(e.status_code)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {Object.keys(e.body || {}).length === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span className="num break-words">
                            {Object.entries(e.body || {})
                              .map(([k, v]) => `${safe(k)}=${safe(v)}`)
                              .join("  ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <TablePagination
          page={page}
          pageCount={Math.ceil(total / pageSize)}
          total={total}
          pageSize={pageSize}
          onPage={(p) => { setPage(p); load(applied, p * pageSize, pageSize); }}
          onPageSize={changePageSize}
          idPrefix="activity"
          noun="request"
        />
        <div className="text-[11px] text-gray-400 mt-2" data-testid="activity-count">
          Oldest entries are dropped by the log retention job.
        </div>
      </Content>
    </>
  );
}